import { useEffect, useState } from 'react'
import Image from 'next/image'

import { cn } from '@/lib/utils'

import { ChevronsUpDown, Check } from 'lucide-react'

import type { ComboboxImage, Props } from './types'

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '../Command'
import { Button } from '../Button'
import { Popover, PopoverContent, PopoverTrigger } from '../Popover'
import { Drawer, DrawerContent, DrawerTrigger } from '../Drawer'

export const Combobox = ({
  value,
  items,
  placeholder,
  emptyResultsMessage,
  onChange,
  className,
  compact = false,
}: Props) => {
  const [valueLocal, setValueLocal] = useState(value)
  const [isSelectOpen, setIsSelectOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(true)

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
    }

    handleResize()
    window.addEventListener('resize', handleResize)

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    setValueLocal(value)
  }, [value])

  const selectedItem = items.find((item) => item.value === valueLocal)

  // Use the displayLabel if it exists, otherwise use the label
  const selectedLabel = selectedItem?.displayLabel ?? selectedItem?.label

  const selectedImage = selectedItem?.image

  const hasImage = (image: ComboboxImage | undefined) =>
    image?.src && image?.alt

  const renderTrigger = () => (
    <Button
      variant="outline"
      role="combobox"
      aria-expanded={isSelectOpen}
      className={cn(
        'h-[50px] w-[200px] min-w-[80px] max-w-full justify-between rounded-md',
        className,
      )}
    >
      {hasImage(selectedImage) && (
        <div className="relative h-[20px] w-[20px]">
          <Image
            src={selectedImage?.src ?? ''}
            fill
            alt={selectedImage?.alt ?? ''}
          />
        </div>
      )}
      <p className={cn(compact && 'hidden overflow-hidden')}>
        {selectedLabel ?? placeholder}
      </p>
      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
    </Button>
  )

  const renderContent = () => (
    <Command
      filter={(filterValue, filterSearch) => {
        const filterLabel = items.find(
          (item) => item.value.toUpperCase() === filterValue.toUpperCase(),
        )?.label

        const lowerCaseLabel = filterLabel?.toLowerCase()

        if (lowerCaseLabel?.includes(filterSearch.toLocaleLowerCase())) {
          return 1
        }

        return 0
      }}
    >
      <CommandInput placeholder={placeholder} />
      <CommandEmpty>{emptyResultsMessage}</CommandEmpty>
      <CommandGroup
        className={`${
          isMobile ? 'h-[300px] max-h-[70vh]' : 'max-h-[300px]'
        } overflow-auto p-2`}
      >
        {items.map((item) => (
          <CommandItem
            key={item.value}
            value={item.value}
            className="hover:text-primary-600 pointer-events-auto cursor-pointer px-2 py-1 hover:bg-slate-100 active:bg-sky-100"
            onSelect={() => {
              const currentValue = item.value === valueLocal ? '' : item.value

              setValueLocal(currentValue)

              onChange?.(currentValue)

              setIsSelectOpen(false)
            }}
          >
            <div className="relative flex gap-2">
              <Check
                className={cn(
                  'mr-2 h-4 w-4',
                  item.value === valueLocal ? 'opacity-100' : 'opacity-0',
                )}
              />

              {hasImage(item?.image) && (
                <div className="relative h-[20px] w-[20px]">
                  <Image
                    src={item?.image?.src ?? ''}
                    fill
                    alt={item?.image?.alt ?? ''}
                  />
                </div>
              )}
              <p>{item.label}</p>
            </div>
          </CommandItem>
        ))}
      </CommandGroup>
    </Command>
  )

  if (isMobile) {
    return (
      <Drawer
        open={isSelectOpen}
        direction="bottom"
        onOpenChange={setIsSelectOpen}
      >
        <DrawerTrigger asChild>{renderTrigger()}</DrawerTrigger>
        <DrawerContent>{renderContent()}</DrawerContent>
      </Drawer>
    )
  }

  return (
    <Popover open={isSelectOpen} onOpenChange={setIsSelectOpen} modal={false}>
      <PopoverTrigger asChild>{renderTrigger()}</PopoverTrigger>
      <PopoverContent
        className="w-[300px] max-w-screen-sm p-0"
        withPortal={false}
      >
        {renderContent()}
      </PopoverContent>
    </Popover>
  )
}
